import {
  EnvironmentProviders,
  Injectable,
  Provider,
  makeEnvironmentProviders,
  inject,
  InjectionToken,
} from '@angular/core';
import {
  RouterFeatures,
  provideRouter,
  Router,
  ActivatedRoute,
  NavigationExtras,
  Params,
  UrlTree,
} from '@angular/router';
import { NamedRoute, RouteStore } from './routes-builder';
import { collectRouteSnapshots } from './route-helper';
import { resolveUrlPath } from '../../../shared-utilities/url-helpers';

const appRouteStoreToken = new InjectionToken('appRouteStore');

// Helper for setting up the injection during Angular bootstrap
export function provideAppRoutes(routeStore: RouteStore, ...features: Array<RouterFeatures>): EnvironmentProviders {
  const routeProvider = provideRouter(routeStore.rootRoutes, ...features);

  // Register an injection token to provide the routeStore.
  const appRoutesProvider: Provider = { provide: appRouteStoreToken, useValue: routeStore };

  // An EnvironmentProvider can only be used during bootstrap, which is what we want.
  return makeEnvironmentProviders([routeProvider, appRoutesProvider]);
}

// This is a wrapper around a routeStore, with additional features for navigating by route name.
@Injectable({
  providedIn: 'root',
})
export class AppRouter {
  private readonly routeStore = inject<RouteStore>(appRouteStoreToken);
  readonly router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  private readonly legacyRouteSegments = ['vl3', 'legacy'];

  get rootRoutes() {
    return this.routeStore.rootRoutes;
  }

  get routes() {
    return this.routeStore.routes;
  }

  get(routeName: string): NamedRoute | undefined {
    return this.routeStore.get(routeName);
  }

  getByFullPath(fullPath: string) {
    return this.routeStore.getByFullPath(fullPath);
  }

  getByUrlPath(urlPath: string) {
    return this.routeStore.getByUrlPath(urlPath);
  }

  getParentName(routeName: string): string | undefined {
    return this.routeStore.getParentName(routeName);
  }

  getParent(routeName: string): NamedRoute | undefined {
    return this.routeStore.getParent(routeName);
  }

  getPath(routeName: string, params?: Params): string {
    if (routeName === '.') return '';

    const namedRoute = this.get(routeName);
    if (!namedRoute?.path) return '';
    return this.resolvePath(namedRoute.path, params);
  }

  getFullPath(routeName: string, params?: Params): string {
    if (routeName === '.') return '';

    const namedRoute = this.get(routeName);
    if (!namedRoute?.fullPath) return '';
    return this.resolvePath(namedRoute.fullPath, params);
  }

  private resolvePath(path: string, params?: Params): string {
    params = this.resolveParams(params);
    return resolveUrlPath(path, params);
  }

  has(routeName: string): boolean {
    return this.routeStore.has(routeName);
  }

  private resolveParams(params?: Params): Params {
    const routeParams = this.collectRouteParams();
    const resolvedParams = { ...routeParams, ...params };
    return resolvedParams;
  }

  private collectRouteParams(): Params {
    const snapshots = collectRouteSnapshots(this.router);
    let activatedParams: Params = {};
    for (const snapshot of snapshots) {
      if (!snapshot.routeConfig) continue;
      activatedParams = { ...activatedParams, ...snapshot.params };
    }
    return activatedParams;
  }

  /*
   * routeName == '.' means the current page
   */
  navigate(routeName: string, navigationOptions?: NavigationOptions): Promise<boolean> {
    //'.' means the current page without a refresh
    if (routeName === '.') routeName = this.current || '';

    navigationOptions ||= {};

    const queryParams = this.urlSearchParamsToParams(navigationOptions.queryParams);
    navigationOptions.queryParams = queryParams;

    const internalRouteName = this.resolveInternalRouteName(routeName);
    const routePath = this.getFullPath(internalRouteName, navigationOptions?.params);
    this.setPublicBrowserUrl(internalRouteName, navigationOptions);

    navigationOptions.state ||= {};
    navigationOptions.state['currentRoute'] = internalRouteName;

    const commands = [];
    if (routePath) commands.push(routePath);

    return this.router.navigate(commands, navigationOptions);
  }

  private urlSearchParamsToParams(searchParams?: URLSearchParams | Params | null): Params | undefined {
    if (!searchParams) return;
    const params: Params = searchParams;
    if (searchParams instanceof URLSearchParams) {
      for (const [key, value] of searchParams) {
        params[key] = value;
      }
    }
    return params;
  }

  createUrlTree(routeName: string, navigationOptions?: NavigationOptions): UrlTree {
    const routePath = this.getFullPath(routeName, navigationOptions?.params);

    if (!routePath) {
      throw new Error(`Route not found: ${routeName}`);
    }

    return this.router.createUrlTree([routePath], navigationOptions);
  }

  // Creates a UrlTree for the browser-facing route. Routes may use `.vl3` or `.legacy` segments
  // for routing, but links should still render the public URL which exclude these segments.
  createPublicUrlTree(routeName: string, navigationOptions?: NavigationOptions): UrlTree {
    const publicRouteName = this.getPublicRouteName(routeName);
    return this.createUrlTree(publicRouteName, navigationOptions);
  }

  // Removes `.vl3` or `.legacy` segments from a route name. This converts internal route names like
  // `main.reports.external-crm.vl3.queued` back to the public `main.reports.external-crm.queued`.
  getPublicRouteName(routeName: string): string {
    if (!routeName) return '';

    const routeSegments = routeName.split('.');
    return routeSegments.filter(segment => !this.legacyRouteSegments.includes(segment)).join('.');
  }

  // Compares routes by their public names so active-link checks still match when the current
  // activated route includes a `.vl3` or `.legacy` segment.
  isSamePublicRoute(routeNameA: string | undefined, routeNameB: string | undefined): boolean {
    if (!routeNameA || !routeNameB) return false;
    return this.getPublicRouteName(routeNameA) === this.getPublicRouteName(routeNameB);
  }

  private resolveInternalRouteName(routeName: string): string {
    const currentRoute = this.current || '';
    const currentRouteSegments = currentRoute.split('.');

    for (const legacyRouteSegment of this.legacyRouteSegments) {
      if (!currentRouteSegments.includes(legacyRouteSegment)) continue;

      const legacyChildRoute = this.findRouteWithSegment(routeName, legacyRouteSegment);
      if (legacyChildRoute && !this.isInCurrentLegacyRouteBranch(legacyChildRoute.name, currentRoute, legacyRouteSegment)) {
        continue;
      }

      if (legacyChildRoute) return legacyChildRoute.name;
    }

    return routeName;
  }

  private isInCurrentLegacyRouteBranch(routeName: string, currentRoute: string, segment: string): boolean {
    const routeSegments = routeName.split('.');
    const segmentIndex = routeSegments.indexOf(segment);
    if (segmentIndex < 0) return false;

    const legacyRouteBranchName = routeSegments.slice(0, segmentIndex + 1).join('.');
    return currentRoute === legacyRouteBranchName || currentRoute.startsWith(`${legacyRouteBranchName}.`);
  }

  private findRouteWithSegment(routeName: string, segment: string): NamedRoute | undefined {
    const segments = routeName.split('.');
    for (let i = segments.length - 1; i >= 0; i--) {
      const segmentsCopy = [...segments];
      segmentsCopy.splice(i + 1, 0, segment);

      const resolvedRouteName = segmentsCopy.join('.');
      const route = this.get(resolvedRouteName);
      if (route) return route;
    }
    return;
  }

  private setPublicBrowserUrl(routeName: string, navigationOptions: NavigationOptions) {
    // Don't assign a browserUrl if the navigation should be skipped or a browserUrl is already provided
    if (navigationOptions?.skipLocationChange) return;
    if (navigationOptions?.browserUrl) return;

    const publicRouteName = this.getPublicRouteName(routeName);
    if (publicRouteName === routeName) return;

    const publicFullPath = this.getFullPath(publicRouteName, navigationOptions.params);
    if (!publicFullPath) return;

    Object.assign(navigationOptions, {
      browserUrl: this.router.createUrlTree([publicFullPath], navigationOptions),
    });
  }

  get current(): string | undefined {
    let currentRoute = this.getCurrentViaSnapshot();
    if (currentRoute) return currentRoute;

    currentRoute = this.currentViaNavigation();
    return currentRoute;
  }

  private getCurrentViaSnapshot(): string | undefined {
    const snapshots = collectRouteSnapshots(this.router);
    const activatedSnapshot = snapshots.pop();
    if (!activatedSnapshot) return;

    const current = activatedSnapshot.routeConfig as NamedRoute;
    return current?.name;
  }

  private currentViaNavigation(): string | undefined {
    const currentNavigation = this.router.currentNavigation();
    if (!currentNavigation) return;

    const navigationState = currentNavigation?.extras?.state;
    if (!navigationState) return;

    const currentRoute = navigationState['currentRoute'];
    return currentRoute;
  }
}

export type NavigationOptions = Omit<NavigationExtras, 'relativeTo'> & {
  params?: Params;
};
